import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import { useMonetizationActions } from 'src/store/monetization/actions';
import {
  DiscountPolicyModel,
  PatchJsonModel,
  PatchJsonData,
  DiscountCodeModel,
  MemberListModel
} from 'src/models/monetizationModel';
import { useUserActions } from 'src/store/users/actions';
import Loader from 'src/components/loader';
import Select, { ActionMeta } from 'react-select';
import makeAnimated from 'react-select/animated';
import { customFilterOption } from 'src/components/utilities/utils';
import { useChannelActions } from 'src/store/channel/actions';
import { AsyncPaginate } from 'react-select-async-paginate';
import { fetchMemberLists } from 'src/components/utilities/lazySpeakerLoader';

interface IProps {
  modalFlag: string;
  currentDiscountId: string;
  setModalFlag: any
}

const DiscountModal: React.FC<IProps> = ({ modalFlag, currentDiscountId, setModalFlag }) => {
  const dispatch = useDispatch();
  const monetizationAction = useMonetizationActions(dispatch);
  const channelAction = useChannelActions(dispatch);
  const userActions = useUserActions(dispatch);

  //get Member Lists and Channel Names
  const [finalChannels, setFinalChannels] = useState<any>();

  //Setting values to state variables
  const [heading, setHeading] = useState('Add');
  const [operation, setOperation] = useState('Add');
  const [codeValue, setCodeValue] = useState('');
  const [attachedMembers, setattachedMembers] = useState<any>([]);

  const btnRef = useRef<HTMLButtonElement>(null);

  //Getting values from RootState
  const {
    isDiscountPolicyAdded,
    channels,
    discountPolicyInfo,
    originalDiscountPolicyInfo,
    discountPolicyDetails,
    loadingDetails,
    loadingCode,
    loadingDiscountMemberList,
    isDiscountCodeAdded,
    isDiscountCodeDeleted,
    isDiscountMemberListAdded,
    isDiscountMemberListDeleted,
    channelList,
    discountId
  } = useSelector((state: RootState) => {
    return {
      isDiscountPolicyAdded: state.monetization.isDiscountPolicyAdded,
      channels: state.channel.channels,
      discountPolicyInfo: state.monetization.discountPolicyInfo,
      originalDiscountPolicyInfo: state.monetization.discountPolicyInfo,
      discountPolicyDetails: state.monetization.discountPolicyDetails,
      loadingDetails: state.monetization.loadingDetails,
      loadingCode: state.monetization.loadingCode,
      loadingDiscountMemberList: state.monetization.loadingDiscountMemberList,
      isDiscountCodeAdded: state.monetization.isDiscountCodeAdded,
      isDiscountCodeDeleted: state.monetization.isDiscountCodeDeleted,
      isDiscountMemberListAdded: state.monetization.isDiscountMemberListAdded,
      isDiscountMemberListDeleted: state.monetization.isDiscountMemberListDeleted,
      channelList: state.channel.channelList,
      discountId: state.monetization.discountId
    };
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control
  } = useForm<DiscountPolicyModel>();


  const effectiveDiscountId = modalFlag === 'edit' ? currentDiscountId : discountId;

  const showExtendedFields = modalFlag === 'edit' || !!discountId;

  //on click of Add/Update Button
  const onSubmit = (data: any) => {
    const payloadData = { ...data, channelId: data?.channelId?.value };
    if (modalFlag === 'add' && !discountId.length) {
      monetizationAction.addDiscountPolicyRequest(payloadData);
    } else if (modalFlag === 'edit' || discountId?.length) {
      const id = payloadData.id ? payloadData.id : discountId
      const patchJsonModel: PatchJsonModel = {
        id,
        patchJsonData: convertModelToPatch(payloadData)
      };
      if (patchJsonModel.patchJsonData.length > 0)
        monetizationAction.editDiscountRequest(patchJsonModel);
    }
  };

  const convertModelToPatch = (data: any) => {
    const patchData = [];

    const excludedFields = ['discountCodes', 'memberLists', 'id'];

    for (const field of Object.keys(data)) {

      if (excludedFields.includes(field)) continue;

      const value: any = originalDiscountPolicyInfo;
      if (value[field] == data[field]) continue;

      const fromValue = Array.isArray(value[field]) || (typeof value[field] === 'object' && value[field] !== null)
        ? null
        : value[field];

      const patchObjdal: PatchJsonData = {
        from: fromValue,
        value: data[field],
        path: '/' + field,
        op: 'replace'
      };
      patchData.push(patchObjdal);
    }
    return patchData;
  };

  const onAttachMember = (newOptions: any) => {
    const prevValues: string[] = attachedMembers.map((m: any) => m.value);
    const newValues: string[] = newOptions.map((m: any) => m.value);

    // Find added item
    const added = newOptions.find((m: any) => !prevValues.includes(m.value));
    // Find removed item
    const removed = attachedMembers.find((m: any) => !newValues.includes(m.value));

    if (added) {
      const addMemberlistModel: MemberListModel = {
        id: added.value,
        entityId: effectiveDiscountId,
        name: added.label
      };
      monetizationAction.addDiscountMemberListRequest(addMemberlistModel);
    }

    if (removed) {
      const deleteMemberlistModel: MemberListModel = {
        id: removed.value,
        entityId: effectiveDiscountId,
        name: removed.label
      };
      monetizationAction.deleteDiscountMemberListRequest(deleteMemberlistModel);
    }

    setattachedMembers(newOptions);
  };

  const handleRemoveCodeClick = (id: any) => {
    const removeCodeModel: DiscountCodeModel = {
      id: id,
      discountId: effectiveDiscountId,
      code: ''
    };
    monetizationAction.deleteDiscountCodeRequest(removeCodeModel);
  };

  const handleAddCodeClick = () => {
    const addCodeModel: DiscountCodeModel = {
      id: '',
      discountId: effectiveDiscountId,
      code: codeValue
    };
    monetizationAction.addDiscountCodeRequest(addCodeModel);
  };

  //On Cancel click
  const handleCancelClick = () => {
    monetizationAction.resetDiscountPolicyDataStateRequest()
    reset({
      name: '',
      percentage: 0,
      channelPercentage: 0,
      message: '',
      channelId: { value: '', label: 'Select Channel' } as any
    });
    setModalFlag('')
    setattachedMembers([]);
  };

  useEffect(() => {
    if (effectiveDiscountId && effectiveDiscountId.length)
      monetizationAction.getDiscountDetails(effectiveDiscountId);
  }, [
    effectiveDiscountId,
    isDiscountCodeAdded,
    isDiscountCodeDeleted,
    isDiscountMemberListAdded,
    isDiscountMemberListDeleted
  ]);

  useEffect(() => {
    if (!discountPolicyDetails || !discountPolicyDetails.memberLists) {
      setattachedMembers([]);
      return;
    }
    const selectedMembers = discountPolicyDetails.memberLists.map((item) => ({
      value: item.id,
      label: item.name
    }));
    setattachedMembers(selectedMembers);
  }, [discountPolicyDetails]);

  //Get the Channel and Memberlists details
  useEffect(() => {
    if (!channels || channels.length == 0) {
      channelAction.getChannelRequest();
    }
  }, []);

  useEffect(() => {
    setCodeValue('');
    if (modalFlag === 'edit') {
      const selectedChannel =
        channelList && channelList.filter((item: any) => item.id === discountPolicyInfo.channelId);
      let dataRef;
      if (selectedChannel) {
        dataRef = {
          value: selectedChannel[0] ? selectedChannel[0].id : '',
          label: selectedChannel[0] ? selectedChannel[0].name : ''
        };
      }
      const finalData = { ...discountPolicyInfo, channelId: dataRef };
      reset(finalData);
      setHeading('Edit');
      setOperation('Update');
    } else {
      if (!discountId.length) {
        setHeading('Add');
        setOperation('Add');
        reset({
          name: '',
          percentage: 0,
          channelPercentage: 0,
          message: '',
          channelId: { value: '', label: 'Select Channel' } as any
        });
        setattachedMembers([]);
      }
    }
  }, [discountPolicyInfo, modalFlag]);

  //Whenever channels value changed, below code will be called
  useEffect(() => {
    channelAction.getFlattenedChannel();
  }, [channels]);

  useEffect(() => {
    if (isDiscountPolicyAdded === true) {
      setHeading('Edit');
      setOperation('Update');
    }
  }, [isDiscountPolicyAdded]);

  useEffect(() => {
    channelAction.getChannelListRequest();
  }, []);

  useEffect(() => {
    if (channelList) {
      const options = channelList.map((item) => ({
        value: item.id,
        label: item.displayText
      }));
      setFinalChannels([
        {
          value: '',
          label: 'Select Channel'
        },
        ...options
      ]);
    }
  }, [channelList]);

  return (
    <div
      className={`modal fade fixed top-0 left-0 hidden w-full h-full outline-none overflow-x-hidden overflow-y-auto modal-open`}
      id="editDiscountPolicyModel"
      aria-labelledby="editDiscountPolicyModelLabel"
      aria-hidden="true">
      <div className="modal-dialog relative w-auto pointer-events-none max-w-xl">
        <div className="modal-content border-none shadow-lg relative flex flex-col w-full pointer-events-auto bg-white bg-clip-padding rounded-md outline-none text-current">
          <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 pb-2 rounded-t-md">
            <h5
              className="text-xl font-semibold leading-normal text-center w-full"
              id="editDiscountPolicyModelLabel">
              {`${heading} Discount Policy Information`}
            </h5>
            <button
              onClick={handleCancelClick}
              type="button"
              className="dismiss-btn"
              data-bs-dismiss="modal"
              aria-label="Close"></button>
          </div>
          <div className="modal-body relative py-4 px-10">
            {loadingDetails ? (
              <Loader />
            ) : (
              <>
                {' '}
                <form>
                  <div className="grid grid-cols-1 xl:gap-4 lg:gap-4 md:gap-2 gap-1 mb-4">
                    <div className={`form-group`}>
                      <label className="block mb-1">Policy Name</label>
                      <input
                        type="text"
                        className="input-style"
                        id="name"
                        {...register('name', {
                          required: true,
                          maxLength: 50
                        })}
                      />
                      {errors.name?.type === 'required' && (
                        <p className="text-red-700">Required Field</p>
                      )}
                      {errors.name?.type === 'maxLength' && (
                        <p className="text-red-700">Max. length (50 characters) reached</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 xl:gap-4 lg:gap-4 md:gap-2 gap-1 mb-4">
                    <div className={`form-group`}>
                      <label className="block mb-1">Applies To</label>
                      {finalChannels && Object.keys(finalChannels).length && (
                        <Controller
                          render={({
                            field: { onChange, onBlur, value, ref },
                            fieldState: { error }
                          }) => (
                            <Select
                              filterOption={customFilterOption}
                              options={finalChannels}
                              value={value}
                              id="channelId"
                              onChange={(val) => onChange(val)}
                            />
                          )}
                          name="channelId"
                          control={control}
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 xl:gap-4 lg:gap-4 md:gap-2 gap-1 mb-4">
                    <div className={`form-group`}>
                      <label className="block mb-1"> Message </label>
                      <input
                        type="text"
                        className="input-style"
                        id="message"
                        {...register('message', {
                          required: false,
                          maxLength: 250
                        })}
                      />

                      {errors.message?.type === 'maxLength' && (
                        <p className="text-red-700">Max. length (250 characters) reached</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 xl:gap-4 lg:gap-4 md:gap-2 gap-1 mb-4">
                    <div className={`form-group`}>
                      <label className="block mb-1"> Channel Percentage </label>
                      <input
                        type="number"
                        className="input-style"
                        id="channelPercentage"
                        {...register('channelPercentage', {
                          required: true,
                          min: 0,
                          max: 100,
                          pattern: /^[0-9.]/
                        })}
                      />
                      {errors.channelPercentage?.type === 'required' && (
                        <p className="text-red-700">Required Field</p>
                      )}
                      {errors.channelPercentage?.type === 'min' && (
                        <p className="text-red-700"> value cannot be lesser than 0</p>
                      )}
                      {errors.channelPercentage?.type === 'max' && (
                        <p className="text-red-700"> value cannot be greater than 100</p>
                      )}
                      {errors.channelPercentage?.type === 'pattern' && (
                        <p className="text-red-700">Only alphabets are allowed</p>
                      )}
                    </div>
                    <div className={`form-group`}>
                      <label className="block mb-1"> Video Percentage </label>
                      <input
                        type="number"
                        className="input-style"
                        id="percentage"
                        {...register('percentage', {
                          required: true,
                          min: 0,
                          max: 100,
                          pattern: /^[0-9.]/
                        })}
                      />
                      {errors.percentage?.type === 'required' && (
                        <p className="text-red-700">Required Field</p>
                      )}
                      {errors.percentage?.type === 'min' && (
                        <p className="text-red-700"> value cannot be lesser than 0</p>
                      )}
                      {errors.percentage?.type === 'max' && (
                        <p className="text-red-700"> value cannot be greater than 100</p>
                      )}
                      {errors.percentage?.type === 'pattern' && (
                        <p className="text-red-700">Only numbers are allowed</p>
                      )}
                    </div>
                  </div>

                  {showExtendedFields && (
                    <>
                      <div className="grid grid-cols-2 xl:gap-4 lg:gap-4 md:gap-2 gap-1 mb-4">
                        <div className={`form-group`}>
                          <div className={`form-group`}>
                            <label className="block mb-1"> Discount Code </label>
                            <input
                              type="text"
                              className="input-style"
                              id="discountCodes"
                              value={codeValue}
                              onChange={(e) => setCodeValue(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex items-center mt-6">
                          <button
                            type="button"
                            onClick={handleAddCodeClick}
                            className="px-7 btn-primary w-28 mr-2 ml-0 mt-0">
                            Add
                          </button>
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="overflow-x-auto">
                          <table className="table-auto w-full border">
                            <thead className="text-xs font-semibold uppercase bg-gray-50">
                              <tr>
                                <th className="p-2">
                                  <div className="font-semibold text-left">Code</div>
                                </th>
                                <th className="p-2">
                                  <div className="font-semibold text-left">Action</div>
                                </th>
                              </tr>
                            </thead>
                            {loadingCode ? (
                              <Loader />
                            ) : (
                              <>
                                {' '}
                                {discountPolicyDetails &&
                                  discountPolicyDetails.discountCodes &&
                                  discountPolicyDetails.discountCodes.length ? (
                                  <tbody className="text-sm divide-y divide-gray-100">
                                    {discountPolicyDetails.discountCodes.map((code, index) => {
                                      return (
                                        <tr key={index}>
                                          <td className="p-2">
                                            <div className="flex items-center">
                                              <div className="font-medium ">{`${code.code}`}</div>
                                            </div>
                                          </td>
                                          <td className="p-2">
                                            <div className="text-left">
                                              <div className="dropdown relative">
                                                <button
                                                  onClick={() =>
                                                    handleRemoveCodeClick(`${code.id}`)
                                                  }
                                                  className="dropdown-toggle"
                                                  type="button"
                                                  id="action-dropdown"
                                                  data-bs-toggle="dropdown"
                                                  aria-expanded="false">
                                                  {' '}
                                                  Delete
                                                </button>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                ) : (
                                  <tr>
                                    <td>No Data Found</td>
                                  </tr>
                                )}
                              </>
                            )}
                          </table>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 xl:gap-4 lg:gap-4 md:gap-2 gap-1 mb-4">
                        <div className={`form-group`}>
                          <label className="block mb-1"> Member Lists </label>
                          {loadingDiscountMemberList ? (
                            <Loader />
                          ) : (
                            <AsyncPaginate
                              isMulti
                              value={attachedMembers}
                              onChange={onAttachMember}
                              placeholder="Select Member Lists"
                              debounceTimeout={300}
                              defaultOptions
                              loadOptions={async (inputValue, _loadedOptions, additional) => {
                                const page = additional?.page || 1;
                                const result = await fetchMemberLists(inputValue, page);
                                return {
                                  options: result.options,
                                  hasMore: result.hasMore,
                                  additional: { page: page + 1 }
                                };
                              }}
                              additional={{ page: 1 }}
                              closeMenuOnSelect={false}
                              menuPlacement="auto"
                              styles={{
                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="modal-footer flex flex-shrink-0 flex-wrap items-center justify-end p-4 px-0 rounded-b-md">
                    <button
                      onClick={handleCancelClick}
                      type="button"
                      ref={btnRef}
                      className="mx-0 px-6 btn-primary discard-btn w-28 mt-0 mr-2"
                      data-bs-dismiss="modal">
                      Close
                    </button>
                    <button
                      style={{ marginRight: '0px' }}
                      type="button"
                      className="px-6 btn-primary w-28 mr-2 ml-0 mt-0"
                      onClick={handleSubmit(onSubmit)}>
                      {operation}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default DiscountModal;